import React, { useState, useRef } from 'react';
import { RotateCcw, Search, Package, AlertTriangle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import './DevolucionesPage.css';

const DevolucionesPage = () => {
    const { user, showToast } = useApp();
    const { hasPermission } = usePermissions();

    // Estado de búsqueda
    const [busqueda, setBusqueda] = useState('');
    const [loading, setLoading] = useState(false);

    // Venta encontrada
    const [venta, setVenta] = useState(null);
    const [articulos, setArticulos] = useState([]);

    // Autorización
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [verificando, setVerificando] = useState(false);
    const [usuarioAutorizador, setUsuarioAutorizador] = useState(null);
    const passwordInputRef = useRef(null);

    // Procesamiento
    const [procesando, setProcesando] = useState(false);

    // Impresión
    const [configImpresion, setConfigImpresion] = useState({});

    // Cargar config de impresión al montar
    React.useEffect(() => {
        const cargarConfig = async () => {
            try {
                const result = await apiService.getConfiguracionesPorCategoria('impresion', null);
                if (result.success && result.data?.valores) {
                    setConfigImpresion(result.data.valores);
                }
            } catch (e) { }
        };
        cargarConfig();
    }, []);

    const obtenerImpresora = async () => {
        const configurada = configImpresion.impresion_impresora_predeterminada;
        if (configurada) return configurada;
        if (window.electron?.ipcRenderer) {
            try {
                const result = await window.electron.ipcRenderer.invoke('get-default-printer');
                if (result.success && result.printer) return result.printer.name;
            } catch (e) { }
        }
        return 'Microsoft Print to PDF';
    };

    // Buscar venta por folio
    const buscarVenta = async () => {
        if (!busqueda.trim()) {
            showToast('Ingresa un folio de venta', 'warning');
            return;
        }

        setLoading(true);
        try {
            const result = await apiService.buscarVentaPorFolio(busqueda.trim());
            if (result.success && result.data) {
                setVenta(result.data);
                const arts = (result.data.detalles || []).map(item => ({
                    ...item,
                    cantidad_devolver: 0,
                    seleccionado: false
                }));
                setArticulos(arts);
            } else {
                showToast(result.message || 'No se encontró la venta', 'error');
                setVenta(null);
                setArticulos([]);
            }
        } catch (error) {
            console.error('Error buscando venta:', error);
            showToast('Error al buscar la venta', 'error');
            setVenta(null);
            setArticulos([]);
        } finally {
            setLoading(false);
        }
    };

    // Actualizar cantidad a devolver
    const actualizarCantidad = (index, cantidad) => {
        const articulo = articulos[index];
        const maxDevolver = articulo.cantidad - (articulo.cantidad_devuelta || 0);
        const cantidadValida = Math.min(Math.max(0, cantidad), maxDevolver);

        setArticulos(prev => prev.map((item, i) =>
            i === index
                ? { ...item, cantidad_devolver: cantidadValida, seleccionado: cantidadValida > 0 }
                : item
        ));
    };

    // Toggle selección de artículo
    const toggleSeleccion = (index) => {
        const articulo = articulos[index];
        const maxDevolver = articulo.cantidad - (articulo.cantidad_devuelta || 0);

        setArticulos(prev => prev.map((item, i) =>
            i === index
                ? {
                    ...item,
                    seleccionado: !item.seleccionado,
                    cantidad_devolver: !item.seleccionado ? maxDevolver : 0
                }
                : item
        ));
    };

    // Iniciar proceso de devolución (pide autorización)
    const iniciarDevolucion = () => {
        const articulosADevolver = articulos.filter(a => a.cantidad_devolver > 0);
        if (articulosADevolver.length === 0) {
            showToast('Selecciona al menos un artículo para devolver', 'warning');
            return;
        }
        setShowPasswordModal(true);
        setTimeout(() => passwordInputRef.current?.focus(), 100);
    };

    // Verificar contraseña de autorización
    const verificarPassword = async () => {
        if (!password) {
            showToast('Ingresa tu contraseña', 'error');
            return;
        }

        setVerificando(true);
        try {
            const result = await apiService.verificarPermisoDevolucion(password);
            if (result.success) {
                setUsuarioAutorizador(result.data?.usuario_nombre || result.data?.nombre || 'N/A');
                setShowPasswordModal(false);
                setPassword('');
                await ejecutarDevolucion(result.data?.usuario_id);
            } else {
                showToast(result.message || 'Acceso denegado', 'error');
            }
        } catch (error) {
            console.error('Error verificando permiso:', error);
            showToast('Error al verificar permiso', 'error');
        } finally {
            setVerificando(false);
        }
    };

    // Ejecutar la devolución
    const ejecutarDevolucion = async (autorizadorId) => {
        const articulosADevolver = articulos.filter(a => a.cantidad_devolver > 0);

        setProcesando(true);
        try {
            const result = await apiService.procesarDevolucion({
                venta_id: venta.id,
                usuario_id: user?.user_id,
                usuario_autorizador_id: autorizadorId,
                articulos: articulosADevolver.map(a => ({
                    detalle_id: a.id,
                    producto_id: a.producto_id,
                    cantidad: a.cantidad_devolver,
                    precio_unitario: a.precio_unitario
                }))
            });

            if (result.success) {
                const folioDevolucion = result.data?.folio || result.data?.id || null;
                const folioVentaOriginal = venta?.folio || venta?.id || 'N/A';
                await imprimirTicketDevolucion(articulosADevolver, folioVentaOriginal, folioDevolucion);

                showToast('Devolución procesada correctamente', 'success');
                limpiar();
            } else {
                showToast(result.message || 'Error al procesar devolución', 'error');
            }
        } catch (error) {
            console.error('Error procesando devolución:', error);
            showToast('Error al procesar la devolución', 'error');
        } finally {
            setProcesando(false);
        }
    };

    // Imprimir ticket de devolución
    const imprimirTicketDevolucion = async (articulosDevueltos, folioVentaOriginal, folioDevolucion) => {
        const fecha = new Date();
        const fechaStr = fecha.toLocaleDateString('es-MX');
        const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const nombrePc = localStorage.getItem('pos_nombre_pc') || 'N/A';
        const usuarioEnCaja = user?.usuario || user?.nombre || user?.username || 'N/A';

        const totalDevolucion = articulosDevueltos.reduce((sum, a) => sum + (a.cantidad_devolver * a.precio_unitario), 0);

        const productosLines = articulosDevueltos.map(a => {
            const nombre = (a.nombre || a.producto_nombre || 'Producto').substring(0, 20).padEnd(20);
            const cant = a.cantidad_devolver.toString().padStart(4);
            const precio = formatMoney(a.precio_unitario).padStart(10);
            const subtotal = formatMoney(a.cantidad_devolver * a.precio_unitario).padStart(10);
            return `${nombre}\n  ${cant} x ${precio} = ${subtotal}`;
        }).join('\n');

        const ticketContent = `
    *** DEVOLUCION ***
====================================
Folio Dev: ${folioDevolucion || 'N/A'}
Venta Orig: ${folioVentaOriginal || 'N/A'}
Fecha: ${fechaStr} ${horaStr}
====================================
Caja: ${nombrePc}
Usuario: ${usuarioEnCaja}
Autorizo: ${usuarioAutorizador || 'N/A'}
====================================
ARTICULOS DEVUELTOS:
------------------------------------
${productosLines}
------------------------------------
TOTAL DEVOLUCION: ${formatMoney(totalDevolucion)}
====================================
`;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Devolucion - ${folioDevolucion || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 12.5px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 0mm 1mm 4mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                        -webkit-font-smoothing: none;
                        text-rendering: geometricPrecision;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                </style>
            </head>
            <body>${ticketContent}</body>
            </html>
        `;

        if (!window.electron?.ipcRenderer) {
            const ventanaImpresion = window.open('', 'Devolucion', 'width=400,height=600');
            if (ventanaImpresion) {
                ventanaImpresion.document.write(htmlContent);
                ventanaImpresion.document.close();
                setTimeout(() => ventanaImpresion.print(), 250);
            }
            return;
        }

        try {
            const printerName = await obtenerImpresora();
            await window.electron.ipcRenderer.invoke('print-document', {
                printerName: printerName,
                htmlContent: htmlContent,
                silent: true,
                options: { margins: { marginType: 'none' }, pageSize: 'A4', dpi: { horizontal: 203, vertical: 203 }, scaleFactor: 100 }
            });
        } catch (error) {
            console.error('Error imprimiendo ticket de devolucion:', error);
        }
    };

    // Limpiar todo
    const limpiar = () => {
        setBusqueda('');
        setVenta(null);
        setArticulos([]);
        setUsuarioAutorizador(null);
    };

    // Calcular total a devolver
    const totalDevolucion = articulos.reduce((sum, a) => sum + (a.cantidad_devolver * a.precio_unitario), 0);
    const cantidadArticulos = articulos.filter(a => a.cantidad_devolver > 0).length;

    // Verificar permiso
    if (!hasPermission('devoluciones_ver')) {
        return (
            <div className="devoluciones-page">
                <div className="devoluciones-no-permission">
                    <AlertTriangle size={48} />
                    <h2>Sin permisos</h2>
                    <p>No tienes permiso para acceder a esta sección.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="devoluciones-page">
            <div className="devoluciones-header">
                <div className="devoluciones-title">
                    <RotateCcw size={24} />
                    <h1>Devoluciones</h1>
                </div>
            </div>

            {/* Búsqueda */}
            <div className="devoluciones-search">
                <label>Buscar venta por folio:</label>
                <div className="devoluciones-search-row">
                    <input
                        type="text"
                        placeholder="Ej: VM-00001 o VTA-00001"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && buscarVenta()}
                        style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.9375rem' }}
                    />
                    <Button onClick={buscarVenta} disabled={loading}>
                        <Search size={16} />
                        {loading ? 'Buscando...' : 'Buscar'}
                    </Button>
                </div>
            </div>

            {/* Venta encontrada */}
            {venta && (
                <div className="devoluciones-content">
                    {/* Info de la venta */}
                    <div className="devoluciones-venta-info">
                        <div className="devoluciones-venta-details">
                            <div className="devoluciones-venta-folio">Venta: {venta.folio}</div>
                            <div className="devoluciones-venta-fecha">
                                {new Date(venta.fecha_venta || venta.created_at).toLocaleString()}
                            </div>
                            {venta.cliente_nombre && (
                                <div className="devoluciones-venta-cliente">Cliente: {venta.cliente_nombre}</div>
                            )}
                        </div>
                        <div className="devoluciones-venta-total">
                            {formatMoney(venta.total)}
                        </div>
                    </div>

                    {/* Tabla de artículos */}
                    <div className="devoluciones-articulos">
                        <div className="devoluciones-articulos-header">
                            Selecciona los articulos a devolver:
                        </div>
                        <div className="devoluciones-articulos-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '5%' }}></th>
                                        <th style={{ textAlign: 'left' }}>Producto</th>
                                        <th>Comprados</th>
                                        <th>Devueltos</th>
                                        <th>Disponibles</th>
                                        <th>Precio Unit.</th>
                                        <th>Devolver</th>
                                        <th>Subtotal Dev.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articulos.map((articulo, index) => {
                                        const maxDevolver = articulo.cantidad - (articulo.cantidad_devuelta || 0);
                                        const subtotalDev = articulo.cantidad_devolver * articulo.precio_unitario;
                                        return (
                                            <tr key={index} className={articulo.seleccionado ? 'selected' : ''}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={articulo.seleccionado}
                                                        onChange={() => toggleSeleccion(index)}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'left', fontWeight: 500 }}>
                                                    {articulo.nombre || articulo.producto_nombre}
                                                </td>
                                                <td>{articulo.cantidad}</td>
                                                <td>{articulo.cantidad_devuelta || 0}</td>
                                                <td style={{ fontWeight: 600, color: maxDevolver > 0 ? '#16a34a' : '#dc2626' }}>
                                                    {maxDevolver}
                                                </td>
                                                <td>{formatMoney(articulo.precio_unitario)}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="devoluciones-qty-input"
                                                        value={articulo.cantidad_devolver}
                                                        onChange={(e) => actualizarCantidad(index, parseInt(e.target.value) || 0)}
                                                        min="0"
                                                        max={maxDevolver}
                                                    />
                                                </td>
                                                <td style={{ fontWeight: 600, color: subtotalDev > 0 ? '#b45309' : '#6b7280' }}>
                                                    {subtotalDev > 0 ? formatMoney(subtotalDev) : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Total a devolver */}
                    {totalDevolucion > 0 && (
                        <div className="devoluciones-total">
                            <div>
                                <div className="devoluciones-total-label">Total a devolver:</div>
                                <div className="devoluciones-total-items">{cantidadArticulos} articulo(s)</div>
                            </div>
                            <div className="devoluciones-total-amount">
                                {formatMoney(totalDevolucion)}
                            </div>
                        </div>
                    )}

                    {/* Botones */}
                    <div className="devoluciones-actions">
                        <Button variant="outline" onClick={limpiar}>
                            Limpiar
                        </Button>
                        <Button
                            onClick={iniciarDevolucion}
                            disabled={procesando || !articulos.some(a => a.cantidad_devolver > 0)}
                        >
                            <RotateCcw size={16} />
                            {procesando ? 'Procesando...' : 'Procesar Devolucion'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Estado vacío */}
            {!venta && !loading && (
                <div className="devoluciones-empty">
                    <Package size={64} />
                    <p>Busca una venta por folio para iniciar una devolucion</p>
                    <span>Ingresa el folio de la venta (mostrador o pedido) en el campo de busqueda</span>
                </div>
            )}

            {/* Modal de autorización */}
            {showPasswordModal && (
                <Modal title="Autorizacion de Devolucion" onClose={() => { setShowPasswordModal(false); setPassword(''); }} size="small">
                    <div style={{ padding: '24px' }}>
                        <p style={{ marginBottom: '16px', color: '#374151' }}>
                            Ingresa tu contrasena para autorizar la devolucion:
                        </p>
                        <input
                            ref={passwordInputRef}
                            type="password"
                            placeholder="Contrasena"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && verificarPassword()}
                            autoFocus
                            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.9375rem' }}
                        />
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button variant="outline" onClick={() => { setShowPasswordModal(false); setPassword(''); }} style={{ flex: 1 }}>
                                Cancelar
                            </Button>
                            <Button onClick={verificarPassword} disabled={verificando} style={{ flex: 1 }}>
                                {verificando ? 'Verificando...' : 'Autorizar'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DevolucionesPage;
